
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Novidade = Database["public"]["Tables"]["novidades"]["Row"];
type Prompt = Database["public"]["Tables"]["prompts"]["Row"];

interface DashboardData {
  prompts: Prompt[];
  novidades: Novidade[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useDashboardData = (): DashboardData => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [novidades, setNovidades] = useState<Novidade[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch in parallel to improve performance
      const [promptsResponse, novidadesResponse] = await Promise.all([
        supabase
          .from("prompts")
          .select("*")
          .limit(3)
          .order("total_uso", { ascending: false }),
        
        supabase
          .from("novidades")
          .select("*")
          .order("criado_em", { ascending: false })
          .limit(3)
      ]);

      if (promptsResponse.error) throw promptsResponse.error;
      if (novidadesResponse.error) throw novidadesResponse.error;

      setPrompts(promptsResponse.data || []);
      setNovidades(novidadesResponse.data || []);
      
    } catch (err: any) {
      console.error("Error fetching dashboard data:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { prompts, novidades, isLoading, error, refetch: fetchData };
};
